import { makeAutoObservable, runInAction } from 'mobx';
import { FavoriteItem, FavoriteType } from '@/types/favorites';
import { addFavoriteAction, deleteFavoriteAction, getFavoritesAction } from '@/utils/favorites.actions';
import { triggerEvent } from '@/sharedTypes/pureHelpers';

// Auth token is not passed here — customFetch (used by apiSwagger inside favorites.actions)
// automatically injects the Bearer token from the /api/auth/token endpoint.
// Components guard login state before calling store methods.

class FavoritesStore {
  favoriteItems: FavoriteItem[] = [];
  favoritesLoaded = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  async handleAddToFavorites(favoriteId: string, favoriteType: FavoriteType) {
    try {
      const res = await addFavoriteAction(favoriteId, favoriteType);
      if (!res) {
        console.error('Failed to add favorite');
        return;
      }
      runInAction(() => {
        this.favoriteItems.push({ type: favoriteType, id: favoriteId });
      });
      console.log('Favorite added successfully: ', favoriteId);
      if (typeof document !== 'undefined') {
        document.dispatchEvent(new CustomEvent('favoriteAdded', { detail: { favoriteId, favoriteType } }));
      }
    } catch (err) {
      console.error('Error adding favorite:', err);
      throw err;
    } finally {
      triggerEvent('favoriteAdded');
    }
  }

  async handleDeleteFromFavorites(favoriteId: string, favoriteType: FavoriteType) {
    if (!favoriteId || !favoriteType) {
      console.error('Invalid favorite data');
      return;
    }

    try {
      const res = await deleteFavoriteAction(favoriteId, favoriteType);
      if (!res) {
        console.error('Failed to delete favorite');
        return;
      }
      runInAction(() => {
        this.favoriteItems = this.favoriteItems.filter(item => item.id !== favoriteId || item.type !== favoriteType);
      });
      console.log('Favorite deleted successfully: ', favoriteId);
      if (typeof document !== 'undefined') {
        document.dispatchEvent(new CustomEvent('favoriteRemoved', { detail: { favoriteId, favoriteType } }));
      }
    } catch (err) {
      console.error('Error deleting favorite:', err);
      throw err;
    } finally {
      triggerEvent('favoriteRemoved');
    }
  }

  async toggleSingleFavorite(favoriteId: string, favoriteType: FavoriteType) {
    const exists = this.favoriteItems.some(item => item.id === favoriteId && item.type === favoriteType);
    try {
      if (exists) {
        await this.handleDeleteFromFavorites(favoriteId, favoriteType);
      } else {
        await this.handleAddToFavorites(favoriteId, favoriteType);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
      throw err;
    }
  }

  // Loads ALL favorites (all types) once. Called on app init.
  // No type filtering on API side — components filter locally via isFavorite().
  async loadInitialFavorites() {
    if (this.favoritesLoaded) return;
    try {
      const res = await getFavoritesAction();
      runInAction(() => {
        this.favoriteItems = res || [];
      });
      console.log('Favorites loaded, count:', (res || []).length);
    } catch (err) {
      console.error('Error loading initial favorites:', err);
    } finally {
      runInAction(() => {
        this.favoritesLoaded = true;
      });
      triggerEvent('favoritesListLoaded');
    }
  }

  isFavorite(favoriteId: string) {
    return this.favoriteItems.some(item => item.id === favoriteId);
  }
}

const favoritesStore = new FavoritesStore();
export default favoritesStore;
